import { useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { TaskList } from '../components/TaskList'
import { useTasks } from '../hooks/useTasks'
import {
  prepareList,
  type SortKey,
  type StatusFilter,
} from '../lib/tree'
import type { TaskPriority } from '../types'

const STATUS_OPTS = ['open', 'done', 'dismissed', 'all'] as const
const SORT_OPTS = ['seen', 'due', 'priority', 'created', 'title'] as const

function parseStatus(raw: string | null): StatusFilter {
  if (raw && (STATUS_OPTS as readonly string[]).includes(raw)) {
    return raw as StatusFilter
  }
  return 'open'
}

function parsePriority(raw: string | null): TaskPriority | 'all' {
  if (raw === 'urgent' || raw === 'high' || raw === 'normal' || raw === 'low') {
    return raw
  }
  return 'all'
}

function parseSort(raw: string | null): SortKey {
  if (raw && (SORT_OPTS as readonly string[]).includes(raw)) {
    return raw as SortKey
  }
  return 'seen'
}

/** Persist list filters in the hash URL so ← Todos restores them. */
function writeFilters(
  params: URLSearchParams,
  patch: {
    status?: StatusFilter
    priority?: TaskPriority | 'all'
    sort?: SortKey
    q?: string
  },
): URLSearchParams {
  const next = new URLSearchParams(params)
  if (patch.status !== undefined) {
    if (patch.status === 'open') next.delete('status')
    else next.set('status', patch.status)
  }
  if (patch.priority !== undefined) {
    if (patch.priority === 'all') next.delete('priority')
    else next.set('priority', patch.priority)
  }
  if (patch.sort !== undefined) {
    if (patch.sort === 'seen') next.delete('sort')
    else next.set('sort', patch.sort)
  }
  if (patch.q !== undefined) {
    if (!patch.q.trim()) next.delete('q')
    else next.set('q', patch.q)
  }
  return next
}

export function TaskListPage() {
  const { optimistic, loading, pending, error, refresh } = useTasks()
  const [params, setParams] = useSearchParams()
  const status = parseStatus(params.get('status'))
  const priority = parsePriority(params.get('priority'))
  const sort = parseSort(params.get('sort'))
  const query = params.get('q') ?? ''

  const setFilter = (patch: Parameters<typeof writeFilters>[1]) => {
    setParams(writeFilters(params, patch), { replace: true })
  }

  const listSearch = params.toString()

  // Survive detail navigations under HashRouter (search is route-local).
  useEffect(() => {
    sessionStorage.setItem('tasks.listSearch', listSearch)
  }, [listSearch])

  const { rows, byId } = useMemo(
    () => prepareList(optimistic, { status, query, priority, sort }),
    [optimistic, status, query, priority, sort],
  )

  const openCount = optimistic.filter((t) => t.status === 'open').length
  const listKey = `${status}:${priority}:${sort}:${query}`

  return (
    <div className="shell">
      <header className="top compact">
        <div className="brand">
          <span className="mark">BeyondQuota</span>
          <h1>Todos</h1>
        </div>
        <p className="lede tight">
          {openCount} open · {optimistic.length} total
        </p>
      </header>

      <div className="controls todoist" role="toolbar" aria-label="Filters and sort">
        <div className="segs" role="group" aria-label="Status">
          {STATUS_OPTS.map((s) => (
            <button
              key={s}
              type="button"
              className={status === s ? 'seg on' : 'seg'}
              aria-pressed={status === s}
              onClick={() => setFilter({ status: s })}
            >
              {s}
            </button>
          ))}
        </div>

        <label className="ctl">
          <span>Priority</span>
          <select
            value={priority}
            onChange={(e) =>
              setFilter({ priority: e.target.value as TaskPriority | 'all' })
            }
          >
            <option value="all">all</option>
            <option value="urgent">urgent</option>
            <option value="high">high</option>
            <option value="normal">normal</option>
            <option value="low">low</option>
          </select>
        </label>

        <label className="ctl">
          <span>Sort</span>
          <select
            value={sort}
            onChange={(e) => setFilter({ sort: e.target.value as SortKey })}
          >
            <option value="seen">recent</option>
            <option value="due">due date</option>
            <option value="priority">priority</option>
            <option value="created">created</option>
            <option value="title">title</option>
          </select>
        </label>

        <label className="ctl grow">
          <span>Search</span>
          <input
            type="search"
            value={query}
            onChange={(e) => setFilter({ q: e.target.value })}
            placeholder="Title, owner, quote…"
            spellCheck={false}
          />
        </label>

        <button
          type="button"
          className={loading || pending ? 'ghost refresh is-busy' : 'ghost refresh'}
          disabled={loading || pending}
          onClick={() => void refresh()}
        >
          {loading || pending ? '…' : 'Refresh'}
        </button>
      </div>

      {error ? (
        <p className="banner" role="alert">
          {error}
          <span className="hint">
            Start the Neon local API (`uv run uvicorn beyondquota.tasks.local_app:app
            --port 8080`).
          </span>
        </p>
      ) : null}

      <main className={loading && !rows.length ? 'list dim' : 'list'}>
        {loading && !rows.length ? (
          <p className="empty">Loading…</p>
        ) : (
          <TaskList
            rows={rows}
            byId={byId}
            listKey={listKey}
            listSearch={listSearch}
          />
        )}
      </main>
    </div>
  )
}
